import { Component, effect, inject, Injector, input, output, signal } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ModelUpdatedEvent,
  RowSelectedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { of } from 'rxjs';
import { CustomAgGridOptions, FieldTypes, IEnumerationCellRendererParams } from '@digiwall/ag-grid';
import { SelectCellEditorComponent, SelectCellEditorParams } from '@digiwall/goliath-core';
import { Utils } from '@digiwall/core';
import { ShapedPartRoleStore } from '@digiwall/goliath-products';
import {
  ProductionCheckListDto,
  ProductionCheckListService,
} from '@digiwall/goliath-products/proxy';
import { CreateProductionLineFromCustomerOrderLineDto } from '@digiwall/goliath-documents/proxy';

/** A creation line augmented with a client-side row id and the row selection flag. */
export type ProductionDocCreationLine = CreateProductionLineFromCustomerOrderLineDto & {
  id: string;
  selected: boolean;
};

@Component({
  selector: 'document-production-doc-creation-grid',
  templateUrl: './production-doc-creation-grid.component.html',
  imports: [AgGridAngular],
})
export class ProductionDocCreationGridComponent {
  private readonly localizationService = inject(LocalizationService);
  private readonly shapedPartRoleStore = inject(ShapedPartRoleStore);
  private readonly checkListService = inject(ProductionCheckListService);
  private readonly injector = inject(Injector);

  readonly lines = input<ProductionDocCreationLine[]>([]);
  /** Emits whether the current selection is savable (>=1 selected and all selected lines fully set). */
  readonly canSaveChange = output<boolean>();

  protected readonly rows = signal<ProductionDocCreationLine[]>([]);

  private readonly checkListsById = new Map<string, ProductionCheckListDto>();
  private initialSelectionDone = false;

  protected readonly defaultColDef: ColDef<ProductionDocCreationLine> = {
    filter: false,
    sortable: false,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  };

  protected readonly columnDefs: ColDef<ProductionDocCreationLine>[] = [
    {
      field: 'displayReference',
      headerName: '#',
      type: FieldTypes.String,
      width: 70,
    },
    {
      field: 'articleCode',
      headerName: this.localizationService.instant('Doc::CustomerReference'),
      type: FieldTypes.String,
      width: 160,
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
      flex: 1,
    },
    {
      field: 'quantity',
      headerName: this.localizationService.instant('::Qty'),
      type: FieldTypes.Number,
      width: 90,
    },
    {
      field: 'shapedPartId',
      headerName: this.localizationService.instant('Doc::ProductionFolderLine:ShapedPart'),
      type: FieldTypes.Enumeration,
      // The enumeration type brings an enterprise multi filter along; this grid has no filtering at all.
      filter: false,
      width: 200,
      cellClassRules: {
        required: params => !!params.data?.selected && !params.data.shapedPartId,
      },
      cellRendererParams: {
        entities: this.shapedPartRoleStore.entities,
      } as IEnumerationCellRendererParams,
      editable: (params: EditableCallbackParams<ProductionDocCreationLine>) =>
        (params.data?.shapedPartIdChoices?.length ?? 0) > 1,
      cellEditor: SelectCellEditorComponent,
      cellEditorParams: {
        values$: (line: ProductionDocCreationLine) => (dto: { queryTerm?: string }) =>
          of({
            items: this.shapedPartRoleStore
              .entities()
              .filter(
                x =>
                  (line.shapedPartIdChoices ?? []).includes(x.id) &&
                  Utils.anyMatches(dto?.queryTerm, x.name.current),
              ),
            hasMore: false,
          }),
        formatOption: (item: { name: { current: string } }) => item?.name?.current ?? '',
        formatSelection: (item: { name: { current: string } }) => item?.name?.current ?? '',
        callback: () => {},
        required: true,
      } as SelectCellEditorParams,
    },
    {
      field: 'checkListId',
      headerName: this.localizationService.instant('Doc::ProductionFolderLine:CheckList'),
      width: 200,
      cellClassRules: {
        required: params => !!params.data?.selected && !params.data.checkListId,
      },
      valueFormatter: (params: ValueFormatterParams<ProductionDocCreationLine>) =>
        this.checkListName(params.value),
      editable: (params: EditableCallbackParams<ProductionDocCreationLine>) =>
        !!params.data?.shapedPartId && this.checkListOptionsFor(params.data).length > 1,
      cellEditor: SelectCellEditorComponent,
      cellEditorParams: {
        values$: (line: ProductionDocCreationLine) => (dto: { queryTerm?: string }) =>
          of({
            items: this.checkListOptionsFor(line).filter(x =>
              Utils.anyMatches(dto?.queryTerm, x.name),
            ),
            hasMore: false,
          }),
        formatOption: (item: ProductionCheckListDto) => item?.name ?? '',
        formatSelection: (item: ProductionCheckListDto) => item?.name ?? '',
        callback: () => {},
        required: true,
      } as SelectCellEditorParams,
    },
  ];

  protected readonly gridOptions: GridOptions<ProductionDocCreationLine> = {
    rowModelType: 'clientSide',
    domLayout: 'autoHeight',
    rowHeight: 30,
    pagination: false,
    suppressContextMenu: true,
    stopEditingWhenCellsLoseFocus: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
    enterNavigatesVertically: true,
    enterNavigatesVerticallyAfterEdit: true,
    getRowId: ({ data }) => data.id,
    components: CustomAgGridOptions.Components,
    columnTypes: CustomAgGridOptions.ColumnTypeDefinitions<ProductionDocCreationLine>(
      this.injector,
    ),
    // Checkbox-only selection: clicking a cell edits it, it never toggles the line.
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: false,
    },
    selectionColumnDef: {
      pinned: 'left',
      lockPinned: true,
      width: 50,
      resizable: false,
    },
    onRowSelected: event => this.onRowSelected(event),
    onCellValueChanged: event => this.onCellValueChanged(event),
    onModelUpdated: event => this.onModelUpdated(event),
  };

  constructor() {
    effect(() => {
      const lines = this.lines() ?? [];
      if (lines.length === 0) {
        this.rows.set([]);
        return;
      }
      this.prepareRows(lines);
    });
  }

  private onRowSelected(event: RowSelectedEvent<ProductionDocCreationLine>): void {
    if (event.data == null) return;
    event.data.selected = event.node.isSelected();
    // The required borders only mark selected lines, so toggling the line must re-evaluate them.
    event.api.refreshCells({
      rowNodes: [event.node],
      columns: ['shapedPartId', 'checkListId'],
      force: true,
    });
    this.emitCanSave();
  }

  private onCellValueChanged(event: CellValueChangedEvent<ProductionDocCreationLine>): void {
    const line = event.data;
    if (event.column.getColId() === 'shapedPartId') {
      // Keep the checklist consistent with the newly chosen shaped part (backend does this at defaults time only).
      this.alignCheckList(line);
      event.api.applyTransaction({ update: [line] });
    }
    this.emitCanSave();
  }

  private onModelUpdated(event: ModelUpdatedEvent<ProductionDocCreationLine>): void {
    if (this.initialSelectionDone || event.api.getDisplayedRowCount() === 0) return;
    // Select every line by default; the user deselects the ones not to produce.
    event.api.forEachNode(node => node.setSelected(true));
    this.initialSelectionDone = true;
  }

  private prepareRows(lines: ProductionDocCreationLine[]): void {
    lines.forEach(line => {
      if (line.id == null) line.id = crypto.randomUUID();
      if (line.selected == null) line.selected = true;
    });

    const shapedPartIds = [...new Set(lines.flatMap(line => line.shapedPartIdChoices ?? []))];
    if (shapedPartIds.length === 0) {
      lines.forEach(line => this.alignCheckList(line));
      this.rows.set([...lines]);
      return;
    }

    // Load every checklist for the shaped parts in play (endRow omitted ⇒ backend returns all, no page cap),
    // so the per-line filtering below is exhaustive even with hundreds of checklists.
    this.checkListService
      .getLookupList({
        shapedPartIds,
        onlyActive: true,
        ids: [],
        queryProperties: ['name'],
        sorting: [{ property: 'name' }],
      })
      .subscribeOnce(result => {
        this.checkListsById.clear();
        (result.items ?? []).forEach(checkList => this.checkListsById.set(checkList.id, checkList));
        // The backend preselects a single checklist choice without looking at the shaped part: re-align it,
        // so a line with no shaped part keeps an empty checklist even when only one choice exists.
        lines.forEach(line => this.alignCheckList(line));
        this.rows.set([...lines]);
      });
  }

  /**
   * Sets the checklist to the only valid option, or clears it when the current value is not offered.
   * No shaped part ⇒ no option ⇒ empty checklist.
   */
  private alignCheckList(line: ProductionDocCreationLine): void {
    const options = this.checkListOptionsFor(line);
    if (options.length === 1) {
      line.checkListId = options[0].id;
    } else if (!options.some(x => x.id === line.checkListId)) {
      line.checkListId = null;
    }
  }

  /** Checklists offered for a line: its checklist choices linked to the currently selected shaped part. */
  private checkListOptionsFor(
    line: ProductionDocCreationLine | undefined,
  ): ProductionCheckListDto[] {
    if (line?.shapedPartId == null) return [];
    const choiceIds = new Set(line.checkListIdChoices ?? []);
    return [...this.checkListsById.values()].filter(
      checkList =>
        choiceIds.has(checkList.id) && (checkList.shapedPartIds ?? []).includes(line.shapedPartId),
    );
  }

  private checkListName(id: string | undefined): string {
    if (!id) return '';
    return this.checkListsById.get(id)?.name ?? '';
  }

  private emitCanSave(): void {
    const selected = (this.lines() ?? []).filter(line => line.selected);
    const canSave =
      selected.length > 0 && selected.every(line => !!line.shapedPartId && !!line.checkListId);
    this.canSaveChange.emit(canSave);
  }
}
